import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CloudOff as MockDataIcon,
  Cloud as LiveDataIcon,
  Compare as CompareIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../../services/api';
import { liveAnalyticsAPI } from '../../services/liveAnalyticsApi';

const DataComparison: React.FC = () => {
  const [showComparison, setShowComparison] = useState(false);

  // Mock data queries (existing endpoints)
  const { data: mockDashboard, isLoading: mockLoading, error: mockError } = useQuery({
    queryKey: ['mockDashboardStats'],
    queryFn: () => analyticsAPI.getDashboardStats(),
    enabled: showComparison
  });

  // Live data queries (new endpoints)
  const { data: liveDashboard, isLoading: liveLoading, error: liveError } = useQuery({
    queryKey: ['liveDashboardStats'],
    queryFn: () => liveAnalyticsAPI.getLiveDashboardStats(),
    enabled: showComparison
  });

  const mockData = mockDashboard?.data || {};
  const liveData = liveDashboard?.data || {};

  if (!showComparison) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Mock Data vs Live Data Comparison
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          This page compares the data returned by mock endpoints vs live SIS database endpoints.
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Note: Mock endpoints require authentication, while live SIS endpoints are publicly accessible for demonstration.
          </Typography>
        </Alert>

        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CompareIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Compare Data Sources
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Click below to load and compare data from both mock and live endpoints
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => setShowComparison(true)}
            >
              Load Data Comparison
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Data Comparison Results
      </Typography>

      {/* Loading States */}
      {(mockLoading || liveLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading data comparison...</Typography>
        </Box>
      )}

      {/* Error States */}
      {(mockError || liveError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <>
            <Typography variant="h6" gutterBottom>API Errors Detected</Typography>
            {mockError && (
              <Typography>
                Mock API Error: {(mockError as any)?.response?.data?.detail || (mockError as any)?.message}
                {(mockError as any)?.response?.status === 401 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Note: Mock endpoints require authentication. Please log in to compare with mock data.
                  </Typography>
                )}
              </Typography>
            )}
            {liveError && (
              <Typography>Live API Error: {(liveError as any)?.response?.data?.detail || (liveError as any)?.message}</Typography>
            )}
          </>
        </Alert>
      )}

      {/* API Endpoint URLs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <MockDataIcon color="warning" sx={{ mr: 1 }} />
                Mock Data Endpoints
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                GET /api/v1/analytics/dashboard-stats/
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Returns calculated/simulated data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LiveDataIcon color="success" sx={{ mr: 1 }} />
                Live Data Endpoints
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                GET /api/v1/sis-integration/live-dashboard-stats/
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Returns data from PostgreSQL at 102.223.7.205:5432
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Comparison */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Mock Data Response
              </Typography>
              <Chip label={`Schools: ${mockData.total_schools || 'N/A'}`} sx={{ mr: 1, mb: 1 }} />
              <Chip label={`Students: ${mockData.total_students || 'N/A'}`} sx={{ mr: 1, mb: 1 }} />
              <Chip label={`Teachers: ${mockData.total_teachers || 'N/A'}`} sx={{ mr: 1, mb: 1 }} />
              <Box sx={{ 
                bgcolor: 'grey.100', 
                p: 2, 
                borderRadius: 1, 
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                maxHeight: 200,
                overflow: 'auto',
                mt: 2
              }}>
                <pre>{JSON.stringify(mockData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Live Data Response
              </Typography>
              <Chip label={`Schools: ${liveData.total_schools || 'N/A'}`} color="success" sx={{ mr: 1, mb: 1 }} />
              <Chip label={`Students: ${liveData.total_students || 'N/A'}`} color="success" sx={{ mr: 1, mb: 1 }} />
              <Chip label={`Teachers: ${liveData.total_teachers || 'N/A'}`} color="success" sx={{ mr: 1, mb: 1 }} />
              <Box sx={{ 
                bgcolor: 'success.50', 
                p: 2, 
                borderRadius: 1, 
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                maxHeight: 200,
                overflow: 'auto',
                mt: 2
              }}>
                <pre>{JSON.stringify(liveData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Conclusion */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>Conclusion</Typography>
        <Typography>
          • The live endpoints connect to the real PostgreSQL database at 102.223.7.205:5432<br/>
          • The mock endpoints return calculated/simulated data<br/>
          • Use the Live Dashboard and Live Analytics pages to see real-time data<br/>
          • The original Analytics page shows the encoded URL parameters issue you identified
        </Typography>
      </Alert>
    </>
  );
};

export default DataComparison;